"use client";

import { useChat } from "@ai-sdk/react";
import type { ChatTransport, FileUIPart, UIMessage } from "ai";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AttachedDocument } from "../../../documents/types";
import type { ModelContextUpdate } from "../../../shared/model-context";
import { hasModelContext } from "../../../shared/model-context";
import type { ChatBaseProps, ChatTransportLifecycle } from "../@types";
import type { PromptInputMessage } from "../ai-elements/prompt-input";
import { useWidgetEvents } from "../embed/widget-events-context";
import { buildApiUrl } from "../lib/api-url";
import { discardDocument } from "../lib/document-upload";
import { LenientChatTransport } from "../lib/lenient-chat-transport";
import {
	deleteThread as deleteThreadFromStore,
	deriveThreadTitle,
	getActiveThreadId,
	listThreads,
	loadThread,
	type StoredThread,
	upsertThread,
} from "../lib/thread-store";
import {
	bearerToken,
	startTurn,
	type TimingTarget,
	type TurnTimer,
} from "../lib/timing";
import type { VisitorContext } from "../lib/visitor-context";
import {
	applyVisitorId,
	collectVisitorContext,
	getOrCreateVisitorId,
} from "../lib/visitor-context";
import { usePageToolCalls } from "./use-page-tool-calls";

const SESSION_HEADER_NAME = "x-session-id";
const THREAD_PERSIST_DEBOUNCE_MS = 250;
const ERROR_MESSAGE_MAX_LENGTH = 200;

function generateThreadId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return nanoid();
}

function nowIso(): string {
	return new Date().toISOString();
}

function normalizeSessionId(value: unknown): string | undefined {
	if (typeof value !== "string") {
		return undefined;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
}

export interface QueuedMessage {
	id: string;
	text: string;
	files: FileUIPart[];
	documents: AttachedDocument[];
	modelContext?: ModelContextUpdate;
}

/**
 * Per-tool definition metadata, keyed by tool name. The chat UI caches the
 * MCP `tools/list` response here so widget resolution
 * (`_meta.ui.resourceUri`, `_meta["openai/outputTemplate"]`, etc.) can
 * happen by tool name, per MCP Apps spec. This is the browser-side
 * equivalent of what a stateful MCP host (e.g. Claude Desktop, MCP Jam)
 * caches for the lifetime of its MCP session.
 */
export type ToolDefinitionsMap = Record<
	string,
	{
		name: string;
		title?: string;
		description?: string;
		_meta?: Record<string, unknown>;
	}
>;

interface ToolsListResponse {
	tools: Array<{
		name: string;
		title?: string;
		description?: string;
		_meta?: Record<string, unknown>;
	}>;
}

async function fetchToolDefinitions(
	api: string,
	headers?: Record<string, string>,
): Promise<ToolDefinitionsMap> {
	const url = buildApiUrl(api, "/tools");
	const response = await fetch(url, {
		method: "GET",
		headers: headers ? { ...headers } : undefined,
	});
	if (!response.ok) {
		throw new Error(
			`[Waniwani] Failed to fetch /tools: ${response.status} ${response.statusText}`,
		);
	}
	const data = (await response.json()) as ToolsListResponse;
	if (!data || !Array.isArray(data.tools)) {
		return {};
	}
	const map: ToolDefinitionsMap = {};
	for (const tool of data.tools) {
		if (tool && typeof tool.name === "string") {
			map[tool.name] = tool;
		}
	}
	return map;
}

type ChatEngineMessage = PromptInputMessage & {
	modelContext?: ModelContextUpdate;
};

export function useChatEngine(props: ChatBaseProps) {
	const {
		api = "/api/waniwani",
		headers: userHeaders,
		uploadHeaders,
		body,
		onMessageSent,
		onResponseReceived,
		enableThreadHistory = false,
		activeThreadId: controlledThreadId,
		onThreadChange,
	} = props;

	// Seed a host-supplied visitor id (e.g. the PostHog / Amplitude distinct id)
	// so every request correlates to the id the host site already tracks. Read
	// live per request downstream, so a changed value takes effect on the next
	// send. Accepts a literal or a sync/async resolver; a blank/failed result is
	// ignored, keeping the auto id. The returned cancel drops a late async result
	// after unmount or a value change. Pass a stable reference (e.g. `useCallback`)
	// for an expensive async resolver so it isn't re-invoked on every render.
	const propVisitorId = props.visitorId;
	useEffect(() => applyVisitorId(propVisitorId), [propVisitorId]);

	// The transport is constructed once at first render and captures that
	// render's callbacks, so the provider must supply a per-mount-stable emitter.
	const widgetEvents = useWidgetEvents();

	const headersRef = useRef(userHeaders);
	const uploadHeadersRef = useRef(uploadHeaders);
	const bodyRef = useRef(body);
	const enableThreadHistoryRef = useRef(enableThreadHistory);
	useEffect(() => {
		enableThreadHistoryRef.current = enableThreadHistory;
	}, [enableThreadHistory]);
	const pendingModelContextRef = useRef<ModelContextUpdate | undefined>(
		undefined,
	);
	const pendingDocumentsRef = useRef<AttachedDocument[] | undefined>(undefined);
	const visitorContextRef = useRef<VisitorContext | null>(null);
	const [sessionId, setSessionIdState] = useState<string | undefined>(
		undefined,
	);
	const sessionIdRef = useRef<string | undefined>(sessionId);

	const [activeThreadId, setActiveThreadIdState] = useState<string | undefined>(
		controlledThreadId,
	);
	const activeThreadIdRef = useRef<string | undefined>(activeThreadId);
	const [threads, setThreads] = useState<StoredThread[]>([]);
	const [isThreadHistoryReady, setIsThreadHistoryReady] = useState(
		!enableThreadHistory,
	);
	const threadCreatedAtRef = useRef<string | undefined>(undefined);
	const threadTitleRef = useRef<string | undefined>(undefined);
	const persistTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	// In-flight `upsertThread` promise. Tracked so callers (delete, flush)
	// can await an already-fired persist and avoid resurrect-after-delete
	// races, which `clearTimeout` alone cannot.
	const persistInflightRef = useRef<Promise<void> | undefined>(undefined);
	// Monotonic epoch invalidator for `switchThread`. Any concurrent
	// `startNewThread` / `switchThread` / `deleteThread` (on the active
	// thread) bumps this so a slower in-flight `loadThread` can't clobber
	// a newer thread's state when it eventually resolves.
	const switchEpochRef = useRef(0);
	const onThreadChangeRef = useRef(onThreadChange);
	useEffect(() => {
		onThreadChangeRef.current = onThreadChange;
	}, [onThreadChange]);

	const setActiveThreadId = useCallback(
		(next: string | undefined) => {
			activeThreadIdRef.current = next;
			setActiveThreadIdState(next);
			if (next) {
				onThreadChangeRef.current?.(next);
				widgetEvents.emit({
					name: "thread.changed",
					// Explicit: the ref is mutated synchronously, while the live
					// getter reads React state and would report the outgoing
					// session after a same-tick restore (switchThread) or clear
					// (startNewThread).
					sessionId: sessionIdRef.current,
					properties: { threadId: next },
				});
			}
		},
		[widgetEvents],
	);

	const refreshThreads = useCallback(async () => {
		if (!enableThreadHistory) {
			return;
		}
		const memoryUserId = visitorContextRef.current?.memoryUserId;
		if (!memoryUserId) {
			return;
		}
		const list = await listThreads(memoryUserId);
		setThreads(list);
	}, [enableThreadHistory]);

	const getSessionId = useCallback((): string | undefined => {
		return sessionIdRef.current;
	}, []);

	const setSessionId = useCallback(
		(value: unknown) => {
			const sessionId = normalizeSessionId(value);
			if (!sessionId) {
				return;
			}
			if (sessionIdRef.current === sessionId) {
				return;
			}

			const isFirstAssignment = sessionIdRef.current === undefined;
			sessionIdRef.current = sessionId;
			setSessionIdState(sessionId);
			if (isFirstAssignment) {
				widgetEvents.emit({
					name: "session.started",
					properties: { sessionId },
				});
			}
		},
		[widgetEvents],
	);

	const clearSessionId = useCallback(() => {
		sessionIdRef.current = undefined;
		setSessionIdState(undefined);
	}, []);

	useEffect(() => {
		headersRef.current = userHeaders;
		uploadHeadersRef.current = uploadHeaders;
	}, [userHeaders, uploadHeaders]);

	useEffect(() => {
		bodyRef.current = body;
	}, [body]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
	useEffect(() => {
		let cancelled = false;
		// Snapshot epoch at mount. Any user action (startNewThread,
		// switchThread, deleteThread on active) bumps it; we then skip
		// applying the stale mount-load result.
		const mountEpoch = switchEpochRef.current;
		(async () => {
			try {
				const ctx = await collectVisitorContext();
				if (cancelled) {
					return;
				}
				visitorContextRef.current = ctx;
			} catch {
				// Best-effort — silently ignore failures
			}

			if (!enableThreadHistory) {
				return;
			}
			const memoryUserId = visitorContextRef.current?.memoryUserId;
			if (!memoryUserId) {
				setIsThreadHistoryReady(true);
				return;
			}

			try {
				const targetId =
					controlledThreadId ?? (await getActiveThreadId(memoryUserId));
				if (cancelled || mountEpoch !== switchEpochRef.current) {
					return;
				}
				if (targetId) {
					const stored = await loadThread(targetId);
					if (cancelled || mountEpoch !== switchEpochRef.current) {
						return;
					}
					if (stored && stored.memoryUserId === memoryUserId) {
						activeThreadIdRef.current = stored.threadId;
						setActiveThreadIdState(stored.threadId);
						threadCreatedAtRef.current = stored.createdAt;
						threadTitleRef.current = stored.title;
						if (stored.sessionId) {
							sessionIdRef.current = stored.sessionId;
							setSessionIdState(stored.sessionId);
						}
						setMessages(stored.messages);
					}
				}
				await refreshThreads();
			} finally {
				if (!cancelled) {
					setIsThreadHistoryReady(true);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const turnRef = useRef<TurnTimer | null>(null);
	const bodyString = useCallback((key: string): string | undefined => {
		const value = bodyRef.current?.[key];
		return typeof value === "string" ? value : undefined;
	}, []);
	const timingTarget = useCallback(
		(): TimingTarget => ({
			api,
			token: bearerToken(headersRef.current),
			channelId: bodyString("channelId"),
			sessionId: sessionIdRef.current,
		}),
		[api, bodyString],
	);
	const reportTurn = useCallback(
		(outcome: "ok" | "error") => {
			const turn = turnRef.current;
			turnRef.current = null;
			turn?.report(timingTarget(), { mode: bodyString("mode"), outcome });
		},
		[timingTarget, bodyString],
	);

	// Auto-inject SDK-managed identifiers into `extra` so they reach MCP
	// `_meta["waniwani/extra"]`. Caller-supplied `body.extra` keys win on collision.
	const mcpExtra = useCallback(
		(callerValue: unknown): Record<string, unknown> | undefined => {
			const callerExtra =
				typeof callerValue === "object" &&
				callerValue !== null &&
				!Array.isArray(callerValue)
					? (callerValue as Record<string, unknown>)
					: undefined;
			const memoryUserId = visitorContextRef.current?.memoryUserId;
			const locale = visitorContextRef.current?.language;
			const autoExtra: Record<string, unknown> = {};
			if (memoryUserId) {
				autoExtra.memoryUserId = memoryUserId;
			}
			if (locale) {
				autoExtra.locale = locale;
			}
			return Object.keys(autoExtra).length > 0 || callerExtra
				? { ...autoExtra, ...(callerExtra ?? {}) }
				: undefined;
		},
		[],
	);

	const ensureThreadId = useCallback((): string => {
		let tid = activeThreadIdRef.current;
		if (!tid) {
			tid = generateThreadId();
			threadCreatedAtRef.current = nowIso();
			threadTitleRef.current = undefined;
			setActiveThreadId(tid);
		}
		return tid;
	}, [setActiveThreadId]);

	const appTransport = useRef(
		new LenientChatTransport({
			api,
			headers: () => ({
				...headersRef.current,
			}),
			body: () => {
				const resolvedBody = {
					...(bodyRef.current ?? {}),
				};

				const hasExplicitSessionId = Object.hasOwn(resolvedBody, "sessionId");
				const bodySessionId = normalizeSessionId(resolvedBody.sessionId);
				if (bodySessionId) {
					setSessionId(bodySessionId);
					resolvedBody.sessionId = bodySessionId;
				} else if (hasExplicitSessionId) {
					clearSessionId();
					delete resolvedBody.sessionId;
				} else {
					const storedSessionId = getSessionId();
					if (storedSessionId) {
						resolvedBody.sessionId = storedSessionId;
					}
				}

				if (hasModelContext(pendingModelContextRef.current)) {
					resolvedBody.modelContext = pendingModelContextRef.current;
				}

				if (pendingDocumentsRef.current?.length) {
					resolvedBody.documents = pendingDocumentsRef.current;
				}

				// Resolve the visitor id synchronously so it is present on the very
				// first request, before the async `collectVisitorContext()` in the
				// mount effect has resolved. The full context (device/client fields)
				// enriches the body once available, but never gates the id. Read it
				// live (not the cached `vc.visitorId`) so a host-supplied override
				// via `setVisitorId()` takes effect on the next request.
				const vc = visitorContextRef.current;
				const visitorId = getOrCreateVisitorId();
				if (vc) {
					resolvedBody.visitorContext = {
						timezone: vc.timezone,
						language: vc.language,
						languages: vc.languages,
						deviceType: vc.deviceType,
						referrer: vc.referrer,
						visitorId,
					};
				}
				const existingVisitor =
					typeof resolvedBody.visitor === "object" &&
					resolvedBody.visitor !== null
						? (resolvedBody.visitor as Record<string, unknown>)
						: {};
				const existingClient =
					typeof existingVisitor.client === "object" &&
					existingVisitor.client !== null
						? (existingVisitor.client as Record<string, unknown>)
						: {};
				resolvedBody.visitor = {
					...existingVisitor,
					// Anonymous visitor id — the server stamps it onto chat events'
					// `visitor_id` so visits link a `page.viewed` to its conversation.
					id: visitorId,
					client: {
						...existingClient,
						...(vc && { memoryUserId: vc.memoryUserId }),
					},
				};

				// Read per send, so it follows a single-page site's in-app navigation.
				if (
					!Object.hasOwn(resolvedBody, "pageUrl") &&
					typeof window !== "undefined"
				) {
					resolvedBody.pageUrl = window.location.href;
				}

				const extra = mcpExtra(resolvedBody.extra);
				if (extra) {
					resolvedBody.extra = extra;
				}

				if (enableThreadHistoryRef.current) {
					const hasExplicitThreadId = Object.hasOwn(resolvedBody, "threadId");
					if (!hasExplicitThreadId) {
						resolvedBody.threadId = ensureThreadId();
					} else if (typeof resolvedBody.threadId === "string") {
						activeThreadIdRef.current = resolvedBody.threadId;
					}
				}

				return resolvedBody;
			},
			fetch: (async (input, init) => {
				const turn = turnRef.current;
				turn?.mark("requestStart");
				try {
					const response = await fetch(input, init);
					pendingModelContextRef.current = undefined;
					setSessionId(response.headers.get(SESSION_HEADER_NAME));
					return turn ? turn.instrument(response) : response;
				} finally {
					// A request that never landed still consumed its documents; the
					// next turn must not re-attach them.
					pendingDocumentsRef.current = undefined;
				}
			}) as typeof fetch,
		}),
	);

	const transportRef = useRef<
		ChatTransport<UIMessage> & ChatTransportLifecycle
	>(
		props.transport?.({
			fallback: appTransport.current,
			headers: () => headersRef.current,
			body: () => {
				const extra = mcpExtra(bodyRef.current?.extra);
				return {
					channelId: bodyString("channelId"),
					visitor: { id: getOrCreateVisitorId() },
					...(extra ? { extra } : {}),
				};
			},
			threadHistory: () => enableThreadHistoryRef.current,
			threadId: () =>
				enableThreadHistoryRef.current ? ensureThreadId() : undefined,
			hasMessages: (): boolean => messagesRef.current.length > 0,
			// The runtime has nowhere to put model context; the chat route drops it too.
			takeTurnInput: () => {
				const documents = pendingDocumentsRef.current;
				pendingDocumentsRef.current = undefined;
				pendingModelContextRef.current = undefined;
				return documents?.length ? { documents } : {};
			},
			onSession: setSessionId,
		}) ?? appTransport.current,
	);

	const pendingWaitRef = useRef<{
		resolve: (msg: unknown) => void;
		reject: (err: Error) => void;
	} | null>(null);
	const finishedMessageRef = useRef<unknown>(null);

	// Tool catalog cached for the lifetime of this ChatCard mount. Fetched
	// once on mount via GET /api/waniwani/tools (spec: the host calls
	// tools/list once per session and caches the result). A mutation counter
	// drives re-renders when the catalog refreshes without making the whole
	// ref reactive.
	const toolDefinitionsRef = useRef<ToolDefinitionsMap>({});
	const [toolDefinitionsRevision, setToolDefinitionsRevision] = useState(0);

	const skipRemoteConfig = props.skipRemoteConfig === true;

	const refreshToolDefinitions = useCallback(async () => {
		if (skipRemoteConfig) {
			return;
		}
		try {
			const map = await fetchToolDefinitions(api, headersRef.current);
			toolDefinitionsRef.current = map;
			setToolDefinitionsRevision((r) => r + 1);
		} catch (error) {
			console.warn(
				"[Waniwani] Failed to fetch tool definitions:",
				error instanceof Error ? error.message : error,
			);
		}
	}, [api, skipRemoteConfig]);

	useEffect(() => {
		void refreshToolDefinitions();
	}, [refreshToolDefinitions]);

	// Inert without `onToolCall`: `pending` stays false and `useChat` gets the
	// same options as it always has.
	const {
		chatRef: pageToolChatRef,
		pending: pageToolCallsPending,
		handleFinish: handPageToolCalls,
		stop: stopPageToolCalls,
		drop: dropPageToolCalls,
		release: releasePageToolCalls,
		markRequest: markPageToolRequest,
	} = usePageToolCalls(props.transport ? undefined : props.onToolCall);

	const {
		messages,
		sendMessage,
		setMessages,
		status,
		stop: stopStream,
		addToolOutput,
	} = useChat({
		messages: props.initialMessages,
		transport: transportRef.current,
		onFinish({ message, isAbort, isDisconnect, isError }) {
			turnRef.current?.mark("streamEnd");
			reportTurn(isAbort || isDisconnect || isError ? "error" : "ok");
			if (!isAbort && !isDisconnect && !isError) {
				handPageToolCalls(message);
			}
			// `onFinish` also runs for aborted/disconnected/errored requests.
			// `onResponseReceived` fires for all of them; the widget event is
			// emitted only for a successful assistant reply.
			onResponseReceived?.();
			if (!isAbort && !isDisconnect && !isError) {
				widgetEvents.emit({ name: "message.received" });
			}
			if (pendingWaitRef.current) {
				// Stash the message — resolve only after React commits the
				// messages state update (see useEffect on `status` below).
				finishedMessageRef.current = message;
			}
		},
		onError(error) {
			reportTurn("error");
			console.warn("[Waniwani] Chat error:", error.message);
			widgetEvents.emit({
				name: "chat.error",
				properties: {
					message: error.message.slice(0, ERROR_MESSAGE_MAX_LENGTH),
				},
			});
			if (pendingWaitRef.current) {
				pendingWaitRef.current.reject(error);
				pendingWaitRef.current = null;
			}
		},
	});

	useEffect(() => {
		pageToolChatRef.current = { addToolOutput, sendMessage };
	}, [pageToolChatRef, addToolOutput, sendMessage]);

	const messagesRef = useRef<UIMessage[]>(messages);
	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	const keepsSession = transportRef.current.keepsSession?.() ?? false;
	const restoredRef = useRef(false);

	// An embed's transport keeps a session only once its remote config answers, after mount.
	// biome-ignore lint/correctness/useExhaustiveDependencies: restore when the transport starts keeping a session
	useEffect(() => {
		if (
			!keepsSession ||
			restoredRef.current ||
			messagesRef.current.length > 0
		) {
			return;
		}
		restoredRef.current = true;
		void transportRef.current
			.restore?.()
			.then((restored) => {
				if (restored.length > 0 && messagesRef.current.length === 0) {
					setMessages(restored);
				}
			})
			.catch(() => {});
	}, [keepsSession]);

	useEffect(() => {
		const transport = transportRef.current;
		return () => transport.dispose?.();
	}, []);

	const beginTurn = useCallback(() => {
		markPageToolRequest();
		turnRef.current = startTurn(
			messagesRef.current.filter((m) => m.role === "user").length + 1,
		);
	}, [markPageToolRequest]);

	// Dropping the stream leaves the server generating. `/cancel` is what stops
	// the turn itself; a host that does not serve it answers 404, which is fine
	// because the abort above already happened.
	// Aborting the stream flips `status` to "ready" straight away, which is the
	// condition the queue drain waits on, while /cancel is still in flight and
	// names only the session. Draining then hands the server a fresh turn for the
	// cancellation to land on. Holding the queue until this settles is what keeps
	// Stop pointed at the turn the visitor stopped.
	const [cancelling, setCancelling] = useState(false);

	const stop = useCallback(async () => {
		// Waiting on the page, the stream has already ended and the server is idle.
		if (await stopPageToolCalls()) {
			return;
		}
		setCancelling(true);
		try {
			await stopStream();
			const sessionId = sessionIdRef.current;
			if (!sessionId) {
				return;
			}
			await fetch(buildApiUrl(api, "/cancel"), {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...headersRef.current,
				},
				body: JSON.stringify({ sessionId }),
			});
		} catch {
			// Same reasoning as a 404: the visitor already got their stop. Nothing
			// here may reject, because the caller is an onClick that drops the promise.
		} finally {
			setCancelling(false);
		}
	}, [api, stopStream, stopPageToolCalls]);

	// Hydrate persisted history when it's enabled *after* mount — commonly via
	// the remote embed config (the dashboard toggle, not a data-attr/prop). The
	// mount effect above ran while history was off and skipped loading, and
	// never re-runs, so a returning visitor's thread would otherwise never
	// appear. Handle the off→on transition here.
	//
	// Only hydrate into an empty chat: never clobber a conversation the visitor
	// started while history was off (those messages live in this same engine).
	// Mirrors the mount effect's load (epoch- and cancel-guarded). Runs once —
	// the ref starts `true` when history was already on at mount (handled there).
	const lateHydratedHistoryRef = useRef(enableThreadHistory);
	// biome-ignore lint/correctness/useExhaustiveDependencies: react to the enableThreadHistory transition only
	useEffect(() => {
		if (!enableThreadHistory || lateHydratedHistoryRef.current) {
			return;
		}
		lateHydratedHistoryRef.current = true;
		if (messagesRef.current.length > 0) {
			return;
		}
		let cancelled = false;
		const epoch = switchEpochRef.current;
		setIsThreadHistoryReady(false);
		(async () => {
			try {
				let ctx = visitorContextRef.current;
				if (!ctx) {
					try {
						ctx = await collectVisitorContext();
						visitorContextRef.current = ctx;
					} catch {
						// Best-effort — silently ignore failures
					}
				}
				const memoryUserId = ctx?.memoryUserId;
				if (!memoryUserId || cancelled || epoch !== switchEpochRef.current) {
					if (!cancelled) {
						setIsThreadHistoryReady(true);
					}
					return;
				}
				const targetId =
					controlledThreadId ?? (await getActiveThreadId(memoryUserId));
				if (cancelled || epoch !== switchEpochRef.current) {
					return;
				}
				if (targetId) {
					const stored = await loadThread(targetId);
					if (cancelled || epoch !== switchEpochRef.current) {
						return;
					}
					if (stored && stored.memoryUserId === memoryUserId) {
						activeThreadIdRef.current = stored.threadId;
						setActiveThreadIdState(stored.threadId);
						threadCreatedAtRef.current = stored.createdAt;
						threadTitleRef.current = stored.title;
						if (stored.sessionId) {
							sessionIdRef.current = stored.sessionId;
							setSessionIdState(stored.sessionId);
						}
						setMessages(stored.messages);
					}
				}
				await refreshThreads();
			} finally {
				if (!cancelled) {
					setIsThreadHistoryReady(true);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [enableThreadHistory]);

	// Resolve sendMessageAndWait only after React has committed the messages
	// state update. `onFinish` fires before the re-render, so resolving there
	// would expose stale `messages` to the caller.
	useEffect(() => {
		if (
			status === "ready" &&
			pendingWaitRef.current &&
			finishedMessageRef.current
		) {
			const pending = pendingWaitRef.current;
			const message = finishedMessageRef.current;
			pendingWaitRef.current = null;
			finishedMessageRef.current = null;
			pending.resolve(message);
		}
	}, [status]);

	const [text, setText] = useState("");
	// A host resolving its own visitor id asynchronously gets its session on the first send instead.
	const prewarm = typeof propVisitorId !== "function";
	const typing = text.length > 0;
	useEffect(() => {
		if (prewarm && typing) {
			transportRef.current.prepare?.();
		}
	}, [prewarm, typing]);
	const [queuedMessages, setQueuedMessages] = useState<QueuedMessage[]>([]);
	const queuedMessagesRef = useRef<QueuedMessage[]>([]);
	queuedMessagesRef.current = queuedMessages;

	// A page still answering tool calls counts as busy, so a message typed
	// meanwhile queues rather than landing after an unanswered tool call.
	const isLoading =
		status === "submitted" || status === "streaming" || pageToolCallsPending;

	/**
	 * A queued message the visitor never sends takes its uploads with it. The
	 * send path must not call this: dequeuing to send is not taking it back.
	 */
	const dropQueued = useCallback(
		(dropped: QueuedMessage[]) => {
			for (const message of dropped) {
				for (const document of message.documents ?? []) {
					void discardDocument({
						documentId: document.documentId,
						api,
						headers: headersRef.current,
						uploadHeaders: uploadHeadersRef.current,
					});
				}
			}
		},
		[api],
	);

	const discardAllQueued = useCallback(() => {
		dropQueued(queuedMessagesRef.current);
		setQueuedMessages([]);
	}, [dropQueued]);

	const removeQueuedMessage = useCallback(
		(id: string) => {
			const found = queuedMessagesRef.current.find((m) => m.id === id);
			if (found) {
				dropQueued([found]);
			}
			setQueuedMessages((prev) => prev.filter((m) => m.id !== id));
		},
		[dropQueued],
	);

	const queueFull = isLoading && queuedMessages.length > 0;

	const handleSubmit = useCallback(
		(message: ChatEngineMessage) => {
			const hasText = Boolean(message.text?.trim());
			const hasFiles = Boolean(
				message.files?.length || message.documents?.length,
			);
			if (!(hasText || hasFiles)) {
				return;
			}

			if (isLoading) {
				// Only allow one queued message at a time
				if (queuedMessages.length > 0) {
					return;
				}

				setQueuedMessages((prev) => [
					...prev,
					{
						id: nanoid(),
						text: message.text || "",
						files: message.files ?? [],
						documents: message.documents ?? [],
						modelContext: message.modelContext,
					},
				]);
				setText("");
				return;
			}

			beginTurn();
			pendingModelContextRef.current = message.modelContext;
			pendingDocumentsRef.current = message.documents;
			sendMessage({
				text: message.text || "",
				files: message.files,
				...(message.documents?.length && {
					metadata: { documents: message.documents },
				}),
			});

			onMessageSent?.(message.text || "");
			widgetEvents.emit({ name: "message.sent" });
			setText("");
		},
		[
			sendMessage,
			onMessageSent,
			isLoading,
			queuedMessages.length,
			widgetEvents,
			beginTurn,
		],
	);

	const sendMessageAndWait = useCallback(
		(text: string): Promise<unknown> => {
			return new Promise((resolve, reject) => {
				pendingWaitRef.current = { resolve, reject };
				beginTurn();
				sendMessage({ text });
				onMessageSent?.(text);
				widgetEvents.emit({ name: "message.sent" });
			});
		},
		[sendMessage, onMessageSent, widgetEvents, beginTurn],
	);

	// Flush first queued message once the current response finishes
	useEffect(() => {
		if (status !== "ready" || cancelling || pageToolCallsPending) {
			return;
		}
		if (queuedMessages.length === 0) {
			return;
		}

		const [first, ...rest] = queuedMessages;
		setQueuedMessages(rest);

		beginTurn();
		pendingModelContextRef.current = first.modelContext;
		pendingDocumentsRef.current = first.documents;
		sendMessage({
			text: first.text,
			files: first.files.length > 0 ? first.files : undefined,
			...(first.documents.length > 0 && {
				metadata: { documents: first.documents },
			}),
		});
		onMessageSent?.(first.text);
		widgetEvents.emit({ name: "message.sent" });
	}, [
		status,
		cancelling,
		pageToolCallsPending,
		sendMessage,
		onMessageSent,
		queuedMessages,
		widgetEvents,
		beginTurn,
	]);

	const reset = useCallback(() => {
		dropPageToolCalls();
		transportRef.current.reset?.();
		setMessages([]);
		discardAllQueued();
		clearSessionId();
		setText("");
		toolDefinitionsRef.current = {};
		setToolDefinitionsRevision((r) => r + 1);
		void refreshToolDefinitions();
	}, [
		setMessages,
		clearSessionId,
		refreshToolDefinitions,
		discardAllQueued,
		dropPageToolCalls,
	]);

	// Build a `StoredThread` from current refs synchronously. Callers that
	// need to flush before mutating thread state (startNewThread,
	// switchThread, deleteThread) must snapshot here *before* any `await`
	// or ref mutation, so the write describes the outgoing thread, not a
	// half-mutated mix of old + new.
	const buildPersistSnapshot = useCallback((): StoredThread | undefined => {
		if (!enableThreadHistoryRef.current) {
			return undefined;
		}
		const memoryUserId = visitorContextRef.current?.memoryUserId;
		const threadId = activeThreadIdRef.current;
		const msgs = messagesRef.current;
		if (!memoryUserId || !threadId || msgs.length === 0) {
			return undefined;
		}
		if (!threadCreatedAtRef.current) {
			threadCreatedAtRef.current = nowIso();
		}
		if (!threadTitleRef.current) {
			const firstUser = msgs.find((m) => m.role === "user");
			const firstUserText = firstUser
				? firstUser.parts
						.map((p) =>
							"text" in p && typeof p.text === "string" ? p.text : "",
						)
						.join(" ")
				: "";
			threadTitleRef.current = deriveThreadTitle(firstUserText);
		}
		return {
			threadId,
			memoryUserId,
			title: threadTitleRef.current,
			messages: msgs,
			sessionId: sessionIdRef.current,
			createdAt: threadCreatedAtRef.current,
			updatedAt: nowIso(),
		};
	}, []);

	const persistActiveThread = useCallback(async () => {
		const snap = buildPersistSnapshot();
		if (!snap) {
			return;
		}
		await upsertThread(snap);
		await refreshThreads();
	}, [buildPersistSnapshot, refreshThreads]);

	useEffect(() => {
		if (!enableThreadHistory) {
			return;
		}
		if (!isThreadHistoryReady) {
			return;
		}
		if (
			status === "submitted" ||
			status === "streaming" ||
			pageToolCallsPending
		) {
			return;
		}
		if (messages.length === 0) {
			return;
		}
		if (persistTimerRef.current) {
			clearTimeout(persistTimerRef.current);
		}
		persistTimerRef.current = setTimeout(() => {
			persistTimerRef.current = undefined;
			const p = persistActiveThread().finally(() => {
				if (persistInflightRef.current === p) {
					persistInflightRef.current = undefined;
				}
			});
			persistInflightRef.current = p;
		}, THREAD_PERSIST_DEBOUNCE_MS);
		return () => {
			if (persistTimerRef.current) {
				clearTimeout(persistTimerRef.current);
			}
		};
	}, [
		enableThreadHistory,
		isThreadHistoryReady,
		messages,
		status,
		pageToolCallsPending,
		persistActiveThread,
	]);

	// Flush in-flight + pending persist. The snapshot is captured *now*
	// (synchronously) so callers can mutate thread refs immediately after
	// invoking this without tainting the outgoing write — even when an
	// in-flight persist forces the async tail to yield first.
	const flushPendingPersist = useCallback((): Promise<void> => {
		const inflight = persistInflightRef.current;
		const timer = persistTimerRef.current;
		const snap = timer ? buildPersistSnapshot() : undefined;
		if (timer) {
			clearTimeout(timer);
			persistTimerRef.current = undefined;
		}
		return (async () => {
			if (inflight) {
				await inflight;
			}
			if (snap) {
				await upsertThread(snap);
				await refreshThreads();
			}
		})();
	}, [buildPersistSnapshot, refreshThreads]);

	const startNewThread = useCallback(() => {
		// Bump epoch first so any in-flight `switchThread` load knows it's
		// been invalidated and skips its setters when it eventually resolves.
		switchEpochRef.current += 1;
		// Fire-and-forget: `flushPendingPersist` snapshots synchronously
		// before any await, so subsequent ref mutations below don't taint
		// the outgoing write.
		void flushPendingPersist();
		dropPageToolCalls();
		// The outgoing thread keeps its runtime session for when it is reopened.
		transportRef.current.dispose?.();
		setMessages([]);
		discardAllQueued();
		clearSessionId();
		setText("");
		const nextId = generateThreadId();
		threadCreatedAtRef.current = nowIso();
		threadTitleRef.current = undefined;
		setActiveThreadId(nextId);
		void refreshThreads();
		return nextId;
	}, [
		setMessages,
		clearSessionId,
		setActiveThreadId,
		refreshThreads,
		flushPendingPersist,
		discardAllQueued,
		dropPageToolCalls,
	]);

	const switchThread = useCallback(
		async (threadId: string) => {
			switchEpochRef.current += 1;
			const epoch = switchEpochRef.current;
			// Stopped first and busy until the switch ends, so the outgoing thread is
			// saved with every tool call answered and no queued message reaches it.
			const stopped = await stopPageToolCalls({ keepBusy: true });
			try {
				if (stopped) {
					// Nothing was saved while the page worked; the updater reads the live messages.
					setMessages((live) => {
						messagesRef.current = live;
						return live;
					});
					await persistActiveThread();
				} else {
					dropPageToolCalls();
				}
				await flushPendingPersist();
				if (epoch !== switchEpochRef.current) {
					return;
				}
				const stored = await loadThread(threadId);
				if (epoch !== switchEpochRef.current) {
					return;
				}
				if (!stored) {
					return;
				}
				transportRef.current.dispose?.();
				// Restore the session before announcing the switch, so the
				// `thread.changed` event carries the target thread's session id.
				if (stored.sessionId) {
					sessionIdRef.current = stored.sessionId;
					setSessionIdState(stored.sessionId);
				} else {
					clearSessionId();
				}
				setActiveThreadId(stored.threadId);
				threadCreatedAtRef.current = stored.createdAt;
				threadTitleRef.current = stored.title;
				setMessages(stored.messages);
				discardAllQueued();
				setText("");
			} finally {
				if (stopped) {
					releasePageToolCalls();
				}
			}
		},
		[
			setMessages,
			clearSessionId,
			setActiveThreadId,
			flushPendingPersist,
			discardAllQueued,
			stopPageToolCalls,
			dropPageToolCalls,
			releasePageToolCalls,
			persistActiveThread,
		],
	);

	const deleteThread = useCallback(
		async (threadId: string) => {
			// Drop pending + await in-flight persist for the thread we're
			// deleting — otherwise an already-fired `upsertThread` can settle
			// after `deleteThreadFromStore` and resurrect the row.
			let stopped = false;
			if (activeThreadIdRef.current === threadId) {
				// Invalidate any in-flight `switchThread` targeting this thread.
				switchEpochRef.current += 1;
				// Stopped first and busy until the delete ends, so a queued message never
				// follows an unanswered tool call.
				stopped = await stopPageToolCalls({ keepBusy: true });
				if (!stopped) {
					dropPageToolCalls();
				}
				if (persistTimerRef.current) {
					clearTimeout(persistTimerRef.current);
					persistTimerRef.current = undefined;
				}
			}
			try {
				const inflight = persistInflightRef.current;
				if (activeThreadIdRef.current === threadId && inflight) {
					await inflight;
				}
				await deleteThreadFromStore(threadId);
				transportRef.current.forget?.(threadId);
				await refreshThreads();
				if (activeThreadIdRef.current === threadId) {
					transportRef.current.dispose?.();
					setMessages([]);
					discardAllQueued();
					clearSessionId();
					setText("");
					activeThreadIdRef.current = undefined;
					setActiveThreadIdState(undefined);
					threadCreatedAtRef.current = undefined;
					threadTitleRef.current = undefined;
				}
			} finally {
				if (stopped) {
					releasePageToolCalls();
				}
			}
		},
		[
			setMessages,
			clearSessionId,
			refreshThreads,
			discardAllQueued,
			stopPageToolCalls,
			dropPageToolCalls,
			releasePageToolCalls,
		],
	);

	// Sync controlled `activeThreadId` after mount. The mount effect seeds
	// the initial value; this effect handles parent-driven changes
	// afterwards by switching threads. Gated on `isThreadHistoryReady` so it
	// runs only once the mount load has settled.
	useEffect(() => {
		if (!enableThreadHistory) {
			return;
		}
		if (!isThreadHistoryReady) {
			return;
		}
		if (controlledThreadId === undefined) {
			return;
		}
		if (controlledThreadId === activeThreadIdRef.current) {
			return;
		}
		void switchThread(controlledThreadId);
	}, [
		controlledThreadId,
		enableThreadHistory,
		isThreadHistoryReady,
		switchThread,
	]);

	const handleTextChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setText(e.target.value);
		},
		[],
	);

	const displayedStatus = pageToolCallsPending ? "streaming" : status;
	const lastMessage = messages[messages.length - 1];
	const hasMessages = messages.length > 0;
	const showLoaderBubble =
		isLoading && (!hasMessages || lastMessage.role === "user");

	// Read through the revision counter so consumers (MessageList) re-render
	// when the catalog refreshes. Taking a snapshot here keeps the returned
	// object referentially stable between revisions.
	void toolDefinitionsRevision;
	const toolDefinitions = toolDefinitionsRef.current;

	return {
		messages,
		status: displayedStatus,
		text,
		setText,
		handleSubmit,
		handleTextChange,
		isLoading,
		showLoaderBubble,
		lastMessage,
		hasMessages,
		sendMessage,
		sendMessageAndWait,
		stop,
		reset,
		queuedMessages,
		queueFull,
		removeQueuedMessage,
		sessionId,
		toolDefinitions,
		refreshToolDefinitions,
		threads,
		activeThreadId,
		isThreadHistoryReady,
		startNewThread,
		switchThread,
		deleteThread,
		keepsSession,
	};
}
